import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export function useSelectedProjectUserMutation({ projectVersionId }: { projectVersionId: string }) {
    return api.useMutation(
        'put',
        '/api/v2/electrical/projects/users/edit',
        {
            onMutate: async (variables) => {
                const body = toPascalCase(variables!.body!);

                await queryClient.cancelQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                    electricalProjectVersionId: projectVersionId,
                    userKind: 'CertifiedInspector',
                }));

                const queryOptions = elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                    electricalProjectVersionId: projectVersionId,
                    userKind: 'CertifiedInspector',
                });
                const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['ListElectricalProjectUserQueryResult'];

                const previouslySelectedUser = previousData.Users.find(user => user.Selected);
                const newData: components['schemas']['ListElectricalProjectUserQueryResult'] = {
                    ...previousData,
                    Users: previousData.Users.map((user) => {
                        if (user.UserId === body.UserId) {
                            return {
                                ...user,
                                Selected: body.Selected,
                            };
                        }
                        else {
                            return {
                                ...user,
                                Selected: false,
                            };
                        }
                    }),
                };

                await queryClient.setQueryData(queryOptions.queryKey, newData);

                logger.debug({
                    message: 'onMutate(/api/v2/electrical/projects/users/edit)',
                    path: '/api/v2/electrical/projects/users/edit',
                    user: {
                        previousData,
                        newData,
                        queryOptions,
                        body,
                    },
                });

                return { previousData, queryOptions, previouslySelectedUser };
            },
            onSettled: async (_data, _error, _variables) => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({
                    electricalProjectVersionId: projectVersionId,
                    userKind: 'CertifiedInspector',
                }));
            },
            scope: {
                id: projectVersionId,
            },
        },
    );
}

type UseCompletionReportPatchParams = {
    projectVersionId: string;
    inspectionKind: components['schemas']['ElectricalInspectionKind'];
};

/**
 * Hook for patching report-level fields (intervalDeterminedByKind, nextInspectionInYearCount,
 * includeRepairStatement, isPerformedAccordingToInspectionPlan, isFinalVerdictPassed, sciosInspectionCode)
 */
export function useCompletionReportPatch({ projectVersionId, inspectionKind }: UseCompletionReportPatchParams) {
    const endpoint = '/api/v2/electrical/projects/inspections/conclusions/reports/patch';

    return usePatcher(endpoint, {
        onMutate: async (variables) => {
            const body = variables!.body!;
            const queryOptions = elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            });

            await queryClient.cancelQueries(queryOptions);

            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'];

            // Update the specific report in the Reports array
            const newData: components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'] = {
                ...previousData,
                Inspection: {
                    ElectricalProjectVersionId: projectVersionId,
                    ...previousData.Inspection,
                    Reports: previousData.Inspection?.Reports?.map((report) => {
                        if (report.InspectionKind === inspectionKind) {
                            return {
                                ...report,
                                ...body,
                            };
                        }
                        return report;
                    }),
                },
            };

            // Update summary data if NextInspectionInYearCount and IsFinalVerdictPassed are present
            if (body.NextInspectionInYearCount !== undefined || body.IsFinalVerdictPassed !== undefined) {
                const recapQueryOptions = elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId: projectVersionId,
                });
                const previousSummaryData = queryClient.getQueryData(recapQueryOptions.queryKey) as components['schemas']['ViewElectricalProjectSummaryQueryResult'];

                if (previousSummaryData) {
                    queryClient.setQueryData<components['schemas']['ViewElectricalProjectSummaryQueryResult']>(recapQueryOptions.queryKey, {
                        ...previousSummaryData,
                        Summary: {
                            ...previousSummaryData.Summary,
                            ElectricalProjectVersionId: projectVersionId,
                            NextInspectionInYearCount: body.NextInspectionInYearCount ?? previousSummaryData?.Summary?.NextInspectionInYearCount,
                            IsFinalVerdictPassed: body.IsFinalVerdictPassed ?? previousSummaryData?.Summary?.IsFinalVerdictPassed,
                        },
                    });
                }
            }

            logger.debug({
                message: `onMutate(${endpoint}) [${inspectionKind}]`,
                path: endpoint,
                conclusion: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            }));
        },
        scope: {
            id: projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectVersionId: projectVersionId,
            InspectionKind: inspectionKind,
        },
    });
}

type UseScope10InspectionPatchParams = {
    projectVersionId: string;
};

/**
 * Hook for patching Scope 10 inspection table cell selection
 * Fields: ElectricalProjectVersionId, NextInspectionInYearCount, Scope10InspectionIntervalTableCellId
 */
export function useScope10InspectionPatch({ projectVersionId }: UseScope10InspectionPatchParams) {
    const endpoint = '/api/v2/electrical/projects/inspections/conclusions/patch';

    return usePatcher(endpoint, {
        onMutate: async (variables) => {
            const body = variables!.body! as {
                Scope10InspectionIntervalTableCellId?: string | null;
                NextInspectionInYearCount?: number | null;
            };

            const queryOptions = elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            });

            await queryClient.cancelQueries(queryOptions);

            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'];

            // Build updated Scope10Inspection with table cell selection
            let updatedScope10Inspection = previousData.Inspection?.Scope10Inspection;

            if (body.Scope10InspectionIntervalTableCellId !== undefined && previousData.Inspection?.Scope10Inspection) {
                updatedScope10Inspection = {
                    ...previousData.Inspection.Scope10Inspection,
                    Table: {
                        ...previousData.Inspection.Scope10Inspection?.Table,
                        Rows: previousData.Inspection.Scope10Inspection?.Table?.Rows?.map(row => ({
                            ...row,
                            Cells: row.Cells?.map((cell) => {
                                if (cell.CellId === body.Scope10InspectionIntervalTableCellId) {
                                    return {
                                        ...cell,
                                        Selected: true,
                                    };
                                }
                                // Deselect other cells
                                return {
                                    ...cell,
                                    Selected: false,
                                };
                            }),
                        })),
                    },
                };
            }

            const newData: components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'] = {
                ...previousData,
                Inspection: {
                    ElectricalProjectVersionId: projectVersionId,
                    ...previousData.Inspection,
                    Scope10Inspection: updatedScope10Inspection,
                    NextInspectionInYearCount: body.NextInspectionInYearCount ?? previousData.Inspection?.NextInspectionInYearCount,
                },
            };

            logger.debug({
                message: `onMutate(${endpoint}) [Scope10]`,
                path: endpoint,
                conclusion: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            }));
        },
        scope: {
            id: projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectVersionId: projectVersionId,
        },
    });
}

type UseIntervalQuestionsPatchParams = {
    projectVersionId: string;
};

/**
 * Hook for patching interval questions (Scope8, Nen1010, Nen3140)
 * Fields: IntervalQuestions nested fields (DetermineInspectionIntervalAge, Quality, Environment, Users, Supervision)
 */
export function useIntervalQuestionsPatch({ projectVersionId }: UseIntervalQuestionsPatchParams) {
    const endpoint = '/api/v2/electrical/projects/inspections/conclusions/patch';

    return usePatcher(endpoint, {
        onMutate: async (variables) => {
            const body = variables!.body! as {
                IntervalQuestions?: {
                    DetermineInspectionIntervalAge?: components['schemas']['ElectricalInspectionDetermineIntervalKind'] | null;
                    DetermineInspectionIntervalQuality?: components['schemas']['ElectricalInspectionDetermineIntervalKind'] | null;
                    DetermineInspectionIntervalEnvironment?: components['schemas']['ElectricalInspectionDetermineIntervalKind'] | null;
                    DetermineInspectionIntervalUsers?: components['schemas']['ElectricalInspectionDetermineIntervalKind'] | null;
                    DetermineInspectionIntervalSupervision?: components['schemas']['ElectricalInspectionDetermineIntervalKind'] | null;
                };
            };

            const queryOptions = elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            });

            await queryClient.cancelQueries(queryOptions);

            const previousData = queryClient.getQueryData(queryOptions.queryKey) as components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'];

            const newData: components['schemas']['EditElectricalProjectInspectionConclusionQueryResult'] = {
                ...previousData,
                Inspection: {
                    ElectricalProjectVersionId: projectVersionId,
                    ...previousData.Inspection,
                    IntervalQuestions: {
                        ...previousData.Inspection?.IntervalQuestions,
                        ...body.IntervalQuestions,
                    },
                },
            };

            logger.debug({
                message: `onMutate(${endpoint}) [Scope8 IntervalQuestions]`,
                path: endpoint,
                conclusion: {
                    previousData,
                    newData,
                    queryOptions,
                    body,
                },
            });

            queryClient.setQueryData(queryOptions.queryKey, newData);

            return { previousData, queryOptions };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({
                electricalProjectVersionId: projectVersionId,
            }));
        },
        scope: {
            id: projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectVersionId: projectVersionId,
        },
    });
}
